import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import api, { unwrap } from '../utils/api';

export default function Products() {
  const [items, setItems] = useState([]);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [categories, setCategories] = useState([]);
  const [tags, setTags] = useState([]);
  const [categoryId, setCategoryId] = useState('');
  const [tag, setTag] = useState('');

  function load() {
    const params = { page, limit: 50 };
    if (search) params.search = search;
    if (categoryId) params.categoryId = categoryId;
    if (tag) params.tag = tag;
    api.get('/products/GetAllProducts', { params }).then(r => {
      const body = unwrap(r);
      setItems(body?.items || []);
      setTotal(body?.total || 0);
    });
  }

  useEffect(load, [page, search, categoryId, tag]);

  // Load filter options once. Categories come from the public list (already
  // sorted by orderIndex); tags are the distinct values currently in use on
  // any product, so the dropdown never offers an option that returns 0 results.
  useEffect(() => {
    api.get('/categories/Categories').then(r => setCategories(unwrap(r) || [])).catch(() => {});
    api.get('/products/GetAllProductTags').then(r => setTags(unwrap(r) || [])).catch(() => {});
  }, []);

  async function clone(id) {
    await api.post('/products/CloneProduct', { _id: id });
    load();
  }
  async function remove(id) {
    if (!window.confirm('Delete this template?')) return;
    await api.post('/products/DeleteProduct', { _id: id });
    load();
  }
  async function togglePublished(p) {
    await api.post('/products/UpdateProduct', { _id: p._id, published: !p.published });
    load();
  }

  async function updateOrder(p, value) {
    const n = Number(value);
    if (!Number.isFinite(n)) return;
    // Optimistic update so the input doesn't snap back while the API call is in flight.
    setItems(prev => prev.map(it => it._id === p._id ? { ...it, productOrderIndex: n } : it));
    try {
      await api.post('/products/UpdateProduct', { _id: p._id, productOrderIndex: n });
    } catch (e) {
      console.error('Failed to update order:', e);
      load();
    }
  }

  return (
    <>
      <h1>Products ({total})</h1>
      <div className="card">
        <div className="row" style={{ marginBottom: 14, gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
          <input placeholder="Search by name" value={search} onChange={e => { setPage(1); setSearch(e.target.value); }} />
          <select
            value={categoryId}
            onChange={e => { setPage(1); setCategoryId(e.target.value); }}
            title="Show only products in this category"
            style={{ padding: '6px 10px' }}
          >
            <option value="">All categories</option>
            {categories.map(c => (
              <option key={c._id} value={c._id}>{c.name}</option>
            ))}
          </select>
          <select
            value={tag}
            onChange={e => { setPage(1); setTag(e.target.value); }}
            title="Show only products with this filter tag"
            style={{ padding: '6px 10px' }}
          >
            <option value="">All filters</option>
            {tags.map(t => (
              <option key={t} value={t}>{t}</option>
            ))}
          </select>
          {(categoryId || tag) && (
            <button
              type="button"
              className="btn small secondary"
              onClick={() => { setPage(1); setCategoryId(''); setTag(''); }}
              title="Clear category and filter selections"
            >
              Clear filters
            </button>
          )}
          <Link className="btn" to="/pages/createProduct">+ New Product</Link>
        </div>
        <table className="table">
          <thead><tr><th>Order</th><th></th><th>Name</th><th>Price</th><th>Languages</th><th>Categories</th><th>Published</th><th>Updated</th><th></th></tr></thead>
          <tbody>
            {items.map(p => (
              <tr key={p._id}>
                <td>
                  <input
                    type="number"
                    value={p.productOrderIndex ?? 0}
                    onChange={e => updateOrder(p, e.target.value)}
                    onBlur={() => load()}
                    style={{ width: 64 }}
                    title="Lower numbers appear first in the customer gallery"
                  />
                </td>
                <td><img className="thumb" src={p.thumbnailWithPlaceholders || p.filesId?.thumbnail} alt="" /></td>
                <td>
                  {p.name}
                  {p.language && p.language !== 'Yiddish' && (
                    <span style={{ marginLeft: 6, fontSize: 11, color: '#888' }}>· {p.language}</span>
                  )}
                </td>
                <td>${Number(p.price).toFixed(2)}</td>
                <td>
                  {/* Filled badge = a variant exists in that language. Click
                      jumps to that variant's edit page. Greyed = missing. */}
                  {['Yiddish', 'English', 'Hebrew'].map(lang => {
                    const present = (p.groupLanguages || []).includes(lang);
                    return (
                      <span
                        key={lang}
                        title={present ? `${lang} variant exists` : `No ${lang} variant yet`}
                        style={{
                          display: 'inline-block', minWidth: 18, padding: '1px 6px', marginRight: 3,
                          fontSize: 11, fontWeight: 600, borderRadius: 4, textAlign: 'center',
                          background: present ? (lang === p.language ? '#1990C7' : '#cfe7f7') : '#eee',
                          color: present ? (lang === p.language ? '#fff' : '#0a5fb4') : '#bbb',
                        }}
                      >
                        {lang[0]}
                      </span>
                    );
                  })}
                </td>
                <td style={{ fontSize: 12 }}>{(p.categoriesIds || []).map(c => c.name).join(', ')}</td>
                <td>
                  <button className="btn small secondary" onClick={() => togglePublished(p)}>{p.published ? 'Yes' : 'No'}</button>
                </td>
                <td style={{ fontSize: 12 }}>{new Date(p.updatedAt).toLocaleDateString()}</td>
                <td>
                  <Link className="btn small" to={`/pages/editProductFields/${p._id}`}>Fields</Link>{' '}
                  <Link className="btn small secondary" to={`/pages/editProduct/${p._id}`}>Edit</Link>{' '}
                  <button className="btn small secondary" onClick={() => clone(p._id)}>Clone</button>{' '}
                  <button className="btn small danger" onClick={() => remove(p._id)}>Del</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        <div className="row" style={{ marginTop: 14 }}>
          <button className="btn small secondary" disabled={page <= 1} onClick={() => setPage(p => p - 1)}>Prev</button>
          <span>Page {page}</span>
          <button className="btn small secondary" disabled={page * 50 >= total} onClick={() => setPage(p => p + 1)}>Next</button>
        </div>
      </div>
    </>
  );
}
